import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@monksflow/monks-ui';
import { ArrowUpRight, Fingerprint } from 'lucide-react';
import {
  taxonomiesQueryOptions,
  taxonomyDetailQueryOptions,
  type LinkedSchemaConfig,
  type TaxonomyField,
} from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';
import { describeLink } from '../../lib/linkedSchema';

export interface LinkedSchemaComposerProps {
  open: boolean;
  /** The linked field being configured. */
  field: TaxonomyField;
  /** This schema's id — excluded from the target picker (no self-links). */
  currentSchemaId: string;
  onClose: () => void;
  onSave: (link: LinkedSchemaConfig) => void;
}

/** Fields of the target schema that carry an actual value (valid pull sources). */
const PULLABLE = new Set<TaxonomyField['type']>(['dropdown', 'free_text', 'date']);

/**
 * Configures a `linked` field: pick a target schema and what to pull from it —
 * the target's unique tracking code (the canonical join key) or a specific
 * field's value/code — plus whether the resolved key is also concatenated into
 * this schema's name (off by default; a join key is a stored reference).
 */
export function LinkedSchemaComposer({
  open,
  field,
  currentSchemaId,
  onClose,
  onSave,
}: LinkedSchemaComposerProps) {
  const { t } = useTranslation('knowledge');
  const link = field.link;
  const [targetSchemaId, setTargetSchemaId] = useState<string | null>(link?.targetSchemaId ?? null);
  const [pullKind, setPullKind] = useState<'tracking_code' | 'field'>(
    link?.pullKind ?? 'tracking_code',
  );
  const [pullFieldId, setPullFieldId] = useState<string | null>(link?.pullFieldId ?? null);
  const [pullSource, setPullSource] = useState<'code' | 'value'>(link?.pullSource ?? 'code');
  const [includeInName, setIncludeInName] = useState<boolean>(link?.includeInName ?? false);

  useEffect(() => {
    if (!open) return;
    setTargetSchemaId(link?.targetSchemaId ?? null);
    setPullKind(link?.pullKind ?? 'tracking_code');
    setPullFieldId(link?.pullFieldId ?? null);
    setPullSource(link?.pullSource ?? 'code');
    setIncludeInName(link?.includeInName ?? false);
  }, [open, link]);

  const { data: schemas = [] } = useQuery(taxonomiesQueryOptions());
  const targets = schemas.filter((s) => s.id !== currentSchemaId);
  const targetName = targets.find((s) => s.id === targetSchemaId)?.name;

  // Target schema detail — only needed to list its fields for a "field" pull.
  const { data: targetDetail } = useQuery({
    ...taxonomyDetailQueryOptions(targetSchemaId ?? ''),
    enabled: !!targetSchemaId && pullKind === 'field',
  });
  const targetFields = useMemo(() => {
    if (!targetDetail) return [];
    const head =
      targetDetail.versions.find((v) => v.id === targetDetail.currentVersionId) ??
      [...targetDetail.versions].sort((a, b) => b.versionNumber - a.versionNumber)[0];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- head falls back to sort()[0], which is undefined when a schema has no versions
    return (head?.fields ?? []).filter((f) => PULLABLE.has(f.type));
  }, [targetDetail]);

  const built: LinkedSchemaConfig = {
    targetSchemaId,
    pullKind,
    ...(pullKind === 'field' ? { pullFieldId, pullSource } : {}),
    includeInName,
  };
  const canSave = !!targetSchemaId && (pullKind !== 'field' || !!pullFieldId);

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.linkedSchemaComposer.configureLinkedSchema')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {/* Preview */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.linkedSchemaComposer.joinKey')}</Label>
          <div className="flex items-center gap-2 rounded-md border border-border-light bg-surface/50 px-3 py-2 font-mono text-sm text-foreground">
            <ArrowUpRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            {describeLink(built, targetName)}
          </div>
        </div>

        {/* Target schema */}
        <div className="flex flex-col gap-1.5">
          <Label>
            {t('taxonomy.linkedSchemaComposer.pullSchema')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          {/* Clearing the field is part of changing the schema: a field id from
              the previous schema still satisfies `canSave` and still gets
              persisted, while the field Select renders blank because the new
              schema has no such field — a dangling reference frozen into the
              published snapshot with nothing on screen to reveal it. */}
          <Select
            value={targetSchemaId ?? ''}
            onValueChange={(v) => {
              setTargetSchemaId(v);
              setPullFieldId(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('taxonomy.linkedSchemaComposer.selectSchema')} />
            </SelectTrigger>
            <SelectContent>
              {targets.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t('taxonomy.linkedSchemaComposer.noOtherSchemasLink')}
                </div>
              ) : (
                targets.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* What to pull */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.linkedSchemaComposer.identifier')}</Label>
          <Select
            value={pullKind}
            onValueChange={(v) => setPullKind(v as 'tracking_code' | 'field')}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tracking_code">
                {t('taxonomy.linkedSchemaComposer.uniqueTrackingCodeRecommended')}
              </SelectItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- LinkedSchemaConfig pullKind enum */}
              <SelectItem value="field">
                {t('taxonomy.linkedSchemaComposer.specificField')}
              </SelectItem>
            </SelectContent>
          </Select>
          {pullKind === 'tracking_code' && (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Fingerprint className="size-3" aria-hidden />
              {t('taxonomy.linkedSchemaComposer.targetSchemaSStandaloneJoin')}
            </span>
          )}
        </div>

        {/* Field + source (only for a field pull) */}
        {pullKind === 'field' && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-48 flex-1 flex-col gap-1.5">
              <Label>
                {t('taxonomy.linkedSchemaComposer.field')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Select value={pullFieldId ?? ''} onValueChange={(v) => setPullFieldId(v)}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      targetSchemaId
                        ? t('taxonomy.linkedSchemaComposer.selectField')
                        : t('taxonomy.linkedSchemaComposer.pickSchemaFirst')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {targetFields.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      {targetSchemaId
                        ? t('taxonomy.linkedSchemaComposer.noPullableFields')
                        : t('taxonomy.linkedSchemaComposer.pickSchemaFirst2')}
                    </div>
                  ) : (
                    targetFields.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-28 flex-col gap-1.5">
              <Label>{t('taxonomy.linkedSchemaComposer.emit')}</Label>
              <Select
                value={pullSource}
                onValueChange={(v) => setPullSource(v as 'code' | 'value')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* eslint-disable-next-line i18next/no-literal-string -- pullSource enum */}
                  <SelectItem value="code">{t('taxonomy.linkedSchemaComposer.code')}</SelectItem>
                  {/* eslint-disable-next-line i18next/no-literal-string -- pullSource enum */}
                  <SelectItem value="value">{t('taxonomy.linkedSchemaComposer.value')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Include in name toggle */}
        <div className="flex items-center justify-between border-t border-border-light pt-4">
          <div className="flex min-w-0 flex-col pr-3">
            <span className="text-sm font-medium">
              {t('taxonomy.linkedSchemaComposer.includeGeneratedName')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.linkedSchemaComposer.offDefaultStoredSeparateReference')}
            </span>
          </div>
          <Switch
            checked={includeInName}
            onCheckedChange={setIncludeInName}
            aria-label={t('taxonomy.linkedSchemaComposer.includeGeneratedName')}
          />
        </div>
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: t('taxonomy.linkedSchemaComposer.saveLink'),
          onClick: () => canSave && onSave(built),
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.linkedSchemaComposer.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

LinkedSchemaComposer.displayName = 'LinkedSchemaComposer';
